// Manager approval of an over-limit discount: the I/O half of
// lib/discountLimit.ts (which holds the rules, the bound tuples and the PIN
// transport).
//
// Flow: the cashier enters a discount over their limit -> the primary button
// reads "Needs manager approval" -> ManagerApprovalDialog, which LISTS every
// value it is about to approve (ApprovalItem: one row per gated line, or the
// receipt value with its base) -> onSubmit(pin) calls
// `approveDiscount(nextCart, items, pin)`, which prices the cart AS IT WILL
// BE on /cart/preview with the PIN attached to exactly those tuples. The
// backend's gate is the judge: success records the approvals (so the PIN
// rides the requests that follow) and the caller then applies the discount;
// an override_* 409 rejects with the backend's message, which the dialog
// shows inline before re-prompting. Nothing is applied on a refusal.
//
// "Success" means the response's `discount_overrides` holds a live row for
// the tuple (confirmedApprovals), not merely a 2xx: only those approvals are
// recorded. A 2xx without a row means the server didn't need a PIN for it.
//
// Every call here is bounded (APPROVAL_TIMEOUT_MS) and refused up front when
// the till is offline (connectionHealthStore, not just navigator.onLine).

import { useSessionStore } from '@/stores/sessionStore'
import { useCartStore } from '@/stores/cartStore'
import { useConnectionHealthStore } from '@/stores/connectionHealthStore'
import { cartToRequest, fetchCartPreview } from '@/api/cart'
import { V2ApiError, classifyPreviewError, v2ErrorCode, v2ErrorText } from '@/api/v2'
import {
  DISCOUNT_EXCEEDS_LIMIT,
  PIN_THROTTLED,
  PIN_THROTTLED_TEXT,
  PinThrottledError,
  addApproval,
  approvalItemsForRefusal,
  approvalsFor,
  approvalsToRecord,
  discountLimitLabel,
  effectiveDiscountLimit,
  eligibleBaseOf,
  isItemApproved,
  isOverrideErrorCode,
  overrideErrorFallback,
  receiptValueKey,
  refusalText,
  type ApprovalItem,
} from '@/lib/discountLimit'
import { cartFingerprint, type Cart, type PreviewResponse } from '@/lib/cart'

export const REMOVE_CART_DISCOUNT_FIRST = 'Remove the cart discount first: item discounts don\'t apply while a cart discount is on.'

export const APPROVAL_NEEDS_CONNECTION = 'Manager approval needs a connection. Try again once the till is back online.'

/** Upper bound on an approval / probe / manager-access call. The dialog
 *  can't be closed while a check is in flight (a late answer must not land
 *  after it's gone), so the call itself must end. */
export const APPROVAL_TIMEOUT_MS = 15_000

class ApprovalTimeoutError extends Error {
  constructor() {
    super(APPROVAL_NEEDS_CONNECTION)
    this.name = 'ApprovalTimeoutError'
  }
}

/** Offline by either signal: the browser's, or the passive connection
 *  health (server unreachable, captive portal) that navigator.onLine misses. */
export function isTillOffline(): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true
  return useConnectionHealthStore.getState().status === 'offline'
}

/** Run `fn` with an AbortSignal that fires after `ms`; a timeout rejects
 *  with the connection message. */
export async function withApprovalTimeout<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  ms: number = APPROVAL_TIMEOUT_MS,
): Promise<T> {
  const ctrl = new AbortController()
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    ctrl.abort()
  }, ms)
  try {
    return await fn(ctrl.signal)
  } catch (err) {
    if (timedOut) throw new ApprovalTimeoutError()
    throw err
  } finally {
    clearTimeout(timer)
  }
}

/** The signed-in clerk's discount limit (100 = none) and its label. */
export function useDiscountLimit(): { limit: number; label: string | null } {
  const staff = useSessionStore((s) => s.staff)
  const limit = effectiveDiscountLimit(staff)
  return { limit, label: discountLimitLabel(limit) }
}

/** Current cart as a `Cart` value (store state minus actions). */
export function cartSnapshot(): Cart {
  const s = useCartStore.getState()
  return {
    customer_id: s.customer_id,
    customer: s.customer,
    exempt_taxes: s.exempt_taxes,
    lines: s.lines,
    preview: s.preview,
    previewStatus: s.previewStatus,
    previewedAt: s.previewedAt,
    partialPayments: s.partialPayments,
    partialAmountToPay: s.partialAmountToPay,
    discount: s.discount,
    loyalty: s.loyalty,
  }
}

/** The receipt base the dialog shows for `cart`: the last preview's eligible
 *  subtotal (that preview may or may not already carry a receipt discount). */
export function currentEligibleBase(cart: Pick<Cart, 'preview' | 'discount'> = cartSnapshot()): number | null {
  return eligibleBaseOf(cart.preview?.totals, receiptValueKey(cart.discount) != null)
}

/** The values the cart banner's Approve asks for: the tuple the 409 named,
 *  else every gated value on the cart (see approvalItemsForRefusal). */
export function gateApprovalItems(extra: Record<string, unknown> | null | undefined): ApprovalItem[] {
  const cart = cartSnapshot()
  return approvalItemsForRefusal(cart, extra, {
    // An errored preview is the last GOOD one, not this cart's: showing its
    // subtotal would tell the manager the wrong base. The approval preview
    // re-binds the real one (withReceiptBase).
    base: cart.previewStatus === 'error' ? null : currentEligibleBase(cart),
    approvals: useCartStore.getState().discountApprovals,
  })
}

/** Dialog rows for `items`. */
export function approvalRows(items: readonly ApprovalItem[]): Array<{ key: string; title: string; detail?: string }> {
  return items.map((i) => ({ key: i.signature, title: i.title, detail: i.detail }))
}

/** Is every one of `items` already approved (an approval for its exact
 *  tuple is riding)? Then re-applying it (e.g. to change the reason) needs
 *  no new PIN. */
export function allApproved(items: readonly ApprovalItem[]): boolean {
  const approvals = useCartStore.getState().discountApprovals
  return items.length > 0 && items.every((i) => isItemApproved(i, approvals))
}

/** Turn a failed approval preview into the Error the dialog shows (a
 *  PinThrottledError locks its submit). `cart`: the cart it was priced
 *  for, to name what a 409 discount_exceeds_limit refused. */
export function approvalError(err: unknown, cart?: Cart): Error {
  if (err instanceof ApprovalTimeoutError) return err
  const code = v2ErrorCode(err)
  if (code === PIN_THROTTLED) return new PinThrottledError(PIN_THROTTLED_TEXT)
  return new Error(approvalErrorMessage(err, cart))
}

function approvalErrorMessage(err: unknown, cart?: Cart): string {
  const code = v2ErrorCode(err)
  if (isOverrideErrorCode(code)) return v2ErrorText(err, overrideErrorFallback(code))
  if (code === DISCOUNT_EXCEEDS_LIMIT) {
    // A value the PIN didn't cover: name it from the 409's extra.
    const extra = err instanceof V2ApiError ? err.extra : null
    return refusalText(extra, cart ?? { lines: [], discount: null })
  }
  const info = classifyPreviewError(err)
  if (info.kind === 'offline' || info.kind === 'transport' || info.kind === 'transient') {
    return APPROVAL_NEEDS_CONNECTION
  }
  if (info.kind === 'auth') return 'Your session is refreshing. Try the PIN again in a moment.'
  return v2ErrorText(err, 'Manager approval failed.')
}

/**
 * Price `nextCart` with `pin` approving exactly `items` (the values the
 * dialog listed). On success, records the approvals the server recorded in
 * cartStore -- receipt approvals bound to the base THIS preview reports --
 * and returns the preview plus the signatures recorded; on a refusal throws
 * an Error with the cashier-facing reason. The caller applies the discount
 * itself after this resolves -- or, when its dialog was closed meanwhile,
 * hands `recorded` to `discardApprovals` instead.
 */
export async function approveDiscount(
  nextCart: Cart,
  items: readonly ApprovalItem[],
  pin: string,
): Promise<{ preview: PreviewResponse; recorded: string[] }> {
  if (items.length === 0) throw new Error('Nothing to approve.')
  // cartToRequest takes every line discount off the wire while a receipt
  // discount is on, so a line PIN would approve nothing.
  if (nextCart.discount && items.some((i) => i.tuple.scope === 'line')) {
    throw new Error(REMOVE_CART_DISCOUNT_FIRST)
  }
  if (isTillOffline()) throw new Error(APPROVAL_NEEDS_CONNECTION)
  const pending = approvalsFor(items, pin)
  let riding = useCartStore.getState().discountApprovals.slice()
  for (const a of pending) riding = addApproval(riding, a)
  let preview: PreviewResponse
  try {
    preview = await withApprovalTimeout(
      (signal) => fetchCartPreview(cartToRequest(nextCart, riding), signal),
    )
  } catch (err) {
    throw approvalError(err, nextCart)
  }
  // Record only what the server recorded (receipt scope bound to the base
  // THIS preview reports). The rest either didn't need a PIN server-side or
  // never reached the gate; keeping their PIN would be a bogus approval
  // riding every later request.
  const store = useCartStore.getState()
  const toRecord = approvalsToRecord(pending, nextCart, preview)
  for (const a of toRecord) store.addDiscountApproval(a)
  // The caller applies exactly this cart next; let the preview hook reuse
  // this response instead of pricing the same basket twice.
  store.primePreview(cartFingerprint(nextCart), preview)
  return { preview, recorded: toRecord.map((a) => a.signature) }
}

/** The dialog was closed while its approval was in flight: drop what that
 *  approval recorded (and the primed preview), so nothing is applied and a
 *  later re-apply of the value can't silently reuse it. */
export function discardApprovals(recorded: readonly string[]): void {
  const store = useCartStore.getState()
  store.removeDiscountApprovals(recorded)
  store.primePreview(null, null)
}

/**
 * Would the server take `nextCart` as it stands (no new PIN)? Used where the
 * POS can't know: a recalled/unheld receipt may already carry an approval
 * for the value. 'needs_approval' only on a 409 `discount_exceeds_limit`;
 * 'unknown' for anything else (offline, timeout, other errors), where the
 * caller applies and lets the cart banner handle it.
 */
export async function probeDiscount(nextCart: Cart): Promise<'ok' | 'needs_approval' | 'unknown'> {
  if (isTillOffline()) return 'unknown'
  try {
    const preview = await withApprovalTimeout(
      (signal) => fetchCartPreview(cartToRequest(nextCart), signal),
    )
    useCartStore.getState().primePreview(cartFingerprint(nextCart), preview)
    return 'ok'
  } catch (err) {
    return v2ErrorCode(err) === DISCOUNT_EXCEEDS_LIMIT ? 'needs_approval' : 'unknown'
  }
}

/**
 * The cart banner's Approve: the discount is already on the cart and the
 * last request 409'd. Approve exactly `items` (gateApprovalItems, listed in
 * the dialog), then land the approved preview so the cart leaves the error
 * state without another round-trip.
 */
export async function approveCartItems(items: readonly ApprovalItem[], pin: string): Promise<void> {
  const snapshot = cartSnapshot()
  const firedFor = cartFingerprint(snapshot)
  const { preview } = await approveDiscount(snapshot, items, pin)
  const store = useCartStore.getState()
  store.applyPreview(preview, firedFor, cartFingerprint(store))
  // Landed directly; nothing for the preview hook to reuse.
  store.primePreview(null, null)
}
